import React, { useState, useEffect, useCallback } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { redirectToCheckout } from '../utils/stripe.js';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import './PremiumSignupBanner.css';

const PremiumSignupBanner = ({ isPremium = false }) => {
  const [isLoading, setIsLoading] = useState(null); // Track which plan is loading
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const { currentUser } = useAuth();

  const checkTrialStatus = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('trial_consumed')
        .eq('id', currentUser.id)
        .single();

      if (!error && data) {
        setHasUsedTrial(data.trial_consumed === true);
      }
    } catch (err) {
      console.error('Error checking trial status:', err);
    }
  }, [currentUser?.id]);

  // Check if user has already used their free trial
  useEffect(() => {
    if (currentUser?.id && !isPremium) {
      checkTrialStatus();
    }
  }, [currentUser?.id, isPremium, checkTrialStatus]);

  // Hide banner for premium users (after hooks are declared)
  if (isPremium) return null;

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 29,
      badge: '7 Years',
      features: [
        'Backtest up to 7 years',
        '25 Strategies',
        '25 Watchlists',
        '100 Saved Backtests',
        '90-day backtest retention',
        '100 AI credits / month (1 credit = 1 chat request)'
      ],
      highlighted: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 49,
      badge: 'Most Popular',
      features: [
        'Backtest up to 15 years',
        '100 Strategies',
        '100 Watchlists',
        '500 Saved Backtests',
        '1-year backtest retention',
        '200 AI credits / month (1 credit = 1 chat request)',
        'Priority support'
      ],
      highlighted: true,
    },
    {
      id: 'ultimate',
      name: 'Ultimate',
      price: 99,
      badge: 'Maximum History',
      features: [
        'Backtest up to 25 years',
        '1,000 Strategies',
        '500 Watchlists',
        '2,000 Saved Backtests',
        'Unlimited backtest retention',
        '1,000 AI credits / month (1 credit = 1 chat request)',
        'Premium support'
      ],
      highlighted: false,
    }
  ];

  const handleStartTrial = async (planId) => {
    try {
      setIsLoading(planId);
      await redirectToCheckout(planId, 'month');
    } catch (error) {
      console.error('Checkout error:', error);
      alert(`Failed to initiate checkout: ${error.message}`);
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className="premium-banner-section">
      <div className="premium-banner-content">
        <div className="premium-banner-header">
          <h2 className="premium-banner-title">Unlock Premium Features</h2>
          <p className="premium-banner-subtitle">
            {hasUsedTrial ? (
              <>All paid plans include a <strong>30-day money-back guarantee</strong></>
            ) : (
              <>All paid plans include a <strong>7-day free trial</strong> and a <strong>30-day money-back guarantee</strong></>
            )}
          </p>
        </div>

        <div className="premium-plans-grid">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`premium-plan-card ${plan.highlighted ? 'highlighted' : ''}`}
            >
              <div className="plan-badge">{plan.badge}</div>

              <h3 className="plan-name">{plan.name}</h3>

              <div className="plan-price">
                <span className="price-amount">${plan.price}</span>
                <span className="price-period">/month</span>
              </div>

              <ul className="plan-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="feature-item">
                    <FiCheckCircle className="feature-icon" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleStartTrial(plan.id)}
                disabled={isLoading !== null}
                className={`plan-cta-button ${plan.highlighted ? 'highlighted-button' : 'default-button'}`}
                style={{ cursor: isLoading === plan.id ? 'not-allowed' : 'pointer', opacity: isLoading === plan.id ? 0.6 : 1 }}
              >
                {isLoading === plan.id ? 'Loading...' : (hasUsedTrial ? 'Subscribe Now' : 'Start Free Trial')}
              </button>

              <p className="plan-note">{hasUsedTrial ? 'Cancel anytime' : '7-day free trial · Cancel anytime'}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PremiumSignupBanner;
